import React from 'react';

interface ContextualItemBadgeProps {
  type: 'goal' | 'plan' | 'workout' | 'mobility' | 'progress' | 'schedule' | 'general' | 'research';
  title: string;
  description?: string;
}

/**
 * ContextualItemBadge - Displays a rich card representing the item being discussed
 * Used in contextual chat launches to show what Cedar has in context
 * Styled after Coach Cedar design language (green accents, clean cards)
 */
export const ContextualItemBadge: React.FC<ContextualItemBadgeProps> = ({
  type,
  title,
  description
}) => {
  // Icon and color for each type
  const typeConfig = {
    goal: {
      icon: '🎯',
      label: 'Goal',
      color: '#1F6C4F', // Cedar Prime (official brand green)
      bgColor: 'rgba(46, 94, 52, 0.05)',
      borderColor: 'rgba(46, 94, 52, 0.2)'
    },
    workout: {
      icon: '💪',
      label: 'Workout',
      color: '#2563eb', // Blue
      bgColor: 'rgba(37, 99, 235, 0.05)',
      borderColor: 'rgba(37, 99, 235, 0.2)'
    },
    plan: {
      icon: '📋',
      label: 'Plan',
      color: '#7c3aed', // Purple
      bgColor: 'rgba(124, 58, 237, 0.05)',
      borderColor: 'rgba(124, 58, 237, 0.2)'
    },
    mobility: {
      icon: '🧘',
      label: 'Mobility',
      color: '#0891b2', // Cyan
      bgColor: 'rgba(8, 145, 178, 0.05)',
      borderColor: 'rgba(8, 145, 178, 0.2)'
    },
    research: {
      icon: '📚',
      label: 'Research',
      color: '#dc2626', // Red
      bgColor: 'rgba(220, 38, 38, 0.05)',
      borderColor: 'rgba(220, 38, 38, 0.2)'
    },
    progress: {
      icon: '📊',
      label: 'Progress',
      color: '#ea580c', // Orange
      bgColor: 'rgba(234, 88, 12, 0.05)',
      borderColor: 'rgba(234, 88, 12, 0.2)'
    },
    schedule: {
      icon: '📅',
      label: 'Schedule',
      color: '#059669', // Emerald
      bgColor: 'rgba(5, 150, 105, 0.05)',
      borderColor: 'rgba(5, 150, 105, 0.2)'
    },
    general: {
      icon: '💬',
      label: 'General',
      color: '#6b7280', // Gray
      bgColor: 'rgba(107, 114, 128, 0.05)',
      borderColor: 'rgba(107, 114, 128, 0.2)'
    }
  };

  const config = typeConfig[type] || typeConfig.general;

  return (
    <div 
      className="rounded-lg p-3 mb-2 border"
      style={{
        backgroundColor: config.bgColor,
        borderColor: config.borderColor
      }}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div 
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg"
          style={{
            backgroundColor: `${config.color}15`,
            color: config.color
          }}
        >
          {config.icon}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Type label */}
          <div 
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mb-1"
            style={{
              backgroundColor: `${config.color}20`,
              color: config.color
            }}
          >
            {config.label}
          </div>
          
          {/* Title */}
          <div className="text-sm font-semibold text-gray-900 mb-1">
            {title}
          </div>
          
          {/* Description (optional) */}
          {description && (
            <div className="text-xs text-gray-600 line-clamp-2">
              {description}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

